import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { dataManagerApi } from '@services/dataManagerApi'
import './AuditLogViewer.css'

interface AuditLogViewerProps {
  companyId: number
}

const TABLE_OPTIONS = [
  'All Tables',
  'data_sales_transactions',
  'data_products',
  'data_sales_pipeline',
  'data_customers',
  'data_customer_interactions',
  'data_customer_reviews',
  'data_chart_of_accounts',
  'data_gl_transactions',
  'data_suppliers',
  'data_employees',
  'data_competitors',
]

const ACTION_OPTIONS = ['All Actions', 'INSERT', 'UPDATE', 'DELETE', 'RESTORE']

function formatTimestamp(value: unknown): string {
  if (!value || typeof value !== 'string') return '-'
  // Show date + time
  const d = new Date(value)
  if (isNaN(d.getTime())) return String(value)
  return d.toISOString().replace('T', ' ').slice(0, 19)
}

function formatJson(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return value
    }
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function AuditLogViewer({ companyId }: AuditLogViewerProps) {
  // Filter state (draft = form values, applied = sent to API)
  const [filterTable, setFilterTable] = useState('All Tables')
  const [filterAction, setFilterAction] = useState('All Actions')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')

  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({})

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Expanded rows (set of row indices)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const queryKey = ['audit-log', companyId, page, pageSize, appliedFilters]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => {
      const params: Record<string, unknown> = {
        page,
        page_size: pageSize,
        ...appliedFilters,
      }
      return dataManagerApi.listAuditLog(companyId, params)
    },
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const handleApply = () => {
    const filters: Record<string, string> = {}
    if (filterTable !== 'All Tables') filters.table_name = filterTable
    if (filterAction !== 'All Actions') filters.action = filterAction
    if (filterDateFrom) filters.date_from = filterDateFrom
    if (filterDateTo) filters.date_to = filterDateTo
    setAppliedFilters(filters)
    setPage(1)
    setExpandedRows(new Set())
  }

  const toggleExpand = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      return next
    })
  }

  return (
    <div className="alv-container">
      <h2 className="alv-header">Audit Log</h2>

      {/* Filter bar */}
      <div className="alv-filters">
        <div className="alv-filter-group">
          <label>Table</label>
          <select value={filterTable} onChange={(e) => setFilterTable(e.target.value)}>
            {TABLE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="alv-filter-group">
          <label>Action</label>
          <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>

        <div className="alv-filter-group">
          <label>From</label>
          <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
        </div>

        <div className="alv-filter-group">
          <label>To</label>
          <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
        </div>

        <button className="alv-apply-btn" onClick={handleApply}>Apply</button>
      </div>

      {/* Table */}
      <div className="alv-table-wrapper">
        {isLoading ? (
          <div className="alv-loading">Loading audit log...</div>
        ) : isError ? (
          <div className="alv-error">Failed to load audit log. Please try again.</div>
        ) : items.length === 0 ? (
          <div className="alv-empty">No audit log entries found.</div>
        ) : (
          <table className="alv-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Table</th>
                <th>Record ID</th>
                <th>Action</th>
                <th>Changed By</th>
                <th>Old Values</th>
                <th>New Values</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => {
                const isExpanded = expandedRows.has(idx)
                const oldJson = formatJson(row.old_values)
                const newJson = formatJson(row.new_values)
                const action = String(row.action ?? '')

                return (
                  <>
                    <tr key={`row-${idx}`}>
                      <td>{formatTimestamp(row.changed_at)}</td>
                      <td>{String(row.table_name ?? '-')}</td>
                      <td>{String(row.record_id ?? '-')}</td>
                      <td>
                        <span className={`alv-badge alv-badge-${action}`}>{action}</span>
                      </td>
                      <td>{String(row.changed_by ?? '-')}</td>
                      <td>
                        {oldJson ? (
                          <button className="alv-view-btn" onClick={() => toggleExpand(idx)}>
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        ) : (
                          <span className="alv-json-empty">-</span>
                        )}
                      </td>
                      <td>
                        {newJson ? (
                          <button className="alv-view-btn" onClick={() => toggleExpand(idx)}>
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        ) : (
                          <span className="alv-json-empty">-</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`expanded-${idx}`} className="alv-expanded-row">
                        <td colSpan={7}>
                          <div className="alv-json-wrapper">
                            <div className="alv-json-section">
                              <div className="alv-json-section-title">Old Values</div>
                              {oldJson ? (
                                <pre className="alv-json-pre">{oldJson}</pre>
                              ) : (
                                <span className="alv-json-empty">No old values</span>
                              )}
                            </div>
                            <div className="alv-json-section">
                              <div className="alv-json-section-title">New Values</div>
                              {newJson ? (
                                <pre className="alv-json-pre">{newJson}</pre>
                              ) : (
                                <span className="alv-json-empty">No new values</span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !isError && items.length > 0 && (
        <div className="alv-pagination">
          <span>
            {total} entr{total !== 1 ? 'ies' : 'y'} total
          </span>
          <div className="alv-pagination-controls">
            <button className="alv-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button className="alv-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
          <div className="alv-page-size">
            <label>
              Show{' '}
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

export default AuditLogViewer
